import { useCallback, useEffect, useState } from "react";
import { Clock, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtPrice } from "@/lib/format";
import { slugify } from "@/lib/constants";
import { NO_CATEGORY_LABEL, categoryOptions, servicesInCategory } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/crm/Badges";
import { ServiceIcon } from "@/components/ServiceIcon";
import { mediaAbs } from "@/lib/media";
import { frameStyle } from "@/lib/framing";
import { ServiceDialog } from "@/components/crm/admin/ServiceDialog";
import { CategoriesPanel } from "@/components/crm/admin/CategoriesPanel";

const ServiceCard = ({ s, category, onToggle, onEdit }) => (
  <div className={`overflow-hidden rounded-2xl border border-slate-200/80 bg-white ${s.active ? "" : "opacity-60"}`} data-testid={`admin-service-${slugify(s.name)}`}>
    <div className="aspect-[16/7] overflow-hidden bg-[#F5F7FA]">{s.image_url ? <img src={mediaAbs(s.image_url)} alt={s.name} className="h-full w-full object-cover" style={frameStyle(s.image_framing)} />
      : <div className="grid h-full place-items-center text-[#1E6FD9]" data-testid={`admin-service-${s.slug || s.id}-placeholder`}><ServiceIcon name={s.icon} className="h-10 w-10" /></div>}</div>
    <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display font-bold"><ServiceIcon name={s.icon} className="h-4 w-4 text-[#1E6FD9]" /> {s.name}</h3>
        <Switch checked={s.active} onCheckedChange={() => onToggle(s)} data-testid={`service-active-toggle-${slugify(s.name)}`} />
      </div>
      <span className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${category ? "bg-[#1E6FD9]/10 text-[#1E6FD9]" : "bg-amber-50 text-amber-800"}`}
            data-testid={`admin-service-category-${slugify(s.name)}`}>{category?.name || NO_CATEGORY_LABEL}</span>
      <p className="mt-2 line-clamp-2 text-sm text-slate-500">{s.description}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="flex items-center gap-1 text-sm text-slate-500"><Clock className="h-4 w-4" /> {s.duration_min} min · <b className="text-[#0B2545]">{fmtPrice(s.price)}</b></span>
        <Button variant="ghost" size="sm" onClick={() => onEdit(s)} data-testid={`service-edit-button-${slugify(s.name)}`}><Pencil className="h-4 w-4" /></Button>
      </div>
    </div>
  </div>
);

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/services").then((r) => setServices(r.data)), []);
  const loadCats = useCallback(() => api.get("/admin/service-categories").then((r) => setCategories(r.data)), []);
  const reloadAll = useCallback(() => { load(); loadCats(); }, [load, loadCats]);
  useEffect(() => { reloadAll(); }, [reloadAll]);

  const toggle = async (s) => {
    try { await api.patch(`/admin/services/${s.id}`, { active: !s.active }); toast.success(s.active ? "Servicio desactivado" : "Servicio activado"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const byId = Object.fromEntries(categories.map((c) => [c.id, c]));

  return (
    <div data-testid="services-admin-page">
      <PageHeader title="Servicios" subtitle="Se reflejan en el sitio público al instante">
        <Button onClick={() => setEditing({})} className="rounded-xl bg-[#1E6FD9]" data-testid="new-service-button"><Plus className="mr-1.5 h-4 w-4" /> Nuevo servicio</Button>
      </PageHeader>
      <Tabs defaultValue="services">
        <TabsList className="mb-5 rounded-xl" data-testid="services-tabs">
          <TabsTrigger value="services" className="rounded-lg" data-testid="services-tab">Servicios</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-lg" data-testid="categories-tab">Categorías de servicios</TabsTrigger>
        </TabsList>
        <TabsContent value="services">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="mb-5 h-10 w-full rounded-xl bg-white sm:w-64" data-testid="services-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categoryOptions(categories).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {servicesInCategory(services, filter).map((s) => <ServiceCard key={s.id} s={s} category={byId[s.category_id]} onToggle={toggle} onEdit={setEditing} />)}
          </div>
          {!servicesInCategory(services, filter).length && <p className="text-sm text-slate-500" data-testid="services-empty">No hay servicios en esta categoría.</p>}
        </TabsContent>
        <TabsContent value="categories"><CategoriesPanel categories={categories} reload={reloadAll} /></TabsContent>
      </Tabs>
      <ServiceDialog service={editing} categories={categories} onClose={() => setEditing(null)} onSaved={reloadAll} />
    </div>
  );
}
